import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const PermissionsContext = createContext(null);

// Permission constants for easy reference
export const RESOURCES = {
  LEADS: 'leads',
  BOOKINGS: 'bookings',
  MASTERS: 'masters',
  REPORTS: 'reports',
  USERS: 'users',
  SETTINGS: 'settings',
  SERVICE: 'service',
};

export const ACTIONS = {
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
  EXPORT: 'export',
};

export function PermissionsProvider({ children, user }) {
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch permissions from API
  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setPermissions(null);
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    if (!token) {
      setPermissions(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/api/auth/permissions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPermissions(data.permissions);
        setError(null);
      } else {
        // Fallback: if endpoint fails, derive permissions from user role
        const fallbackPermissions = getFallbackPermissions(user);
        setPermissions(fallbackPermissions);
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      // Fallback on error
      const fallbackPermissions = getFallbackPermissions(user);
      setPermissions(fallbackPermissions);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Check if user has permission for a specific action on a resource
  const hasPermission = useCallback((resource, action) => {
    if (!permissions) return true; // Allow if permissions not loaded (backward compat)
    
    // Super admin has all permissions
    if (permissions.is_super_admin) return true;
    
    const resourcePermissions = permissions[resource];
    if (!resourcePermissions) return false;
    
    return resourcePermissions.includes(action) || resourcePermissions.includes('*');
  }, [permissions]);

  // Check if user can create a resource
  const canCreate = useCallback((resource) => hasPermission(resource, ACTIONS.CREATE), [hasPermission]);
  
  // Check if user can read a resource
  const canRead = useCallback((resource) => hasPermission(resource, ACTIONS.READ), [hasPermission]);
  
  // Check if user can update a resource
  const canUpdate = useCallback((resource) => hasPermission(resource, ACTIONS.UPDATE), [hasPermission]);
  
  // Check if user can delete a resource
  const canDelete = useCallback((resource) => hasPermission(resource, ACTIONS.DELETE), [hasPermission]);
  
  // Check if user can export reports
  const canExport = useCallback((resource) => hasPermission(resource, ACTIONS.EXPORT), [hasPermission]);

  // Get user's role name
  const roleName = user?.role_name || 'Unknown';
  const isSuperAdmin = user?.is_super_admin || false;
  const isCompanyAdmin = roleName === 'Company Admin' || isSuperAdmin;
  const isManager = roleName === 'Manager' || isCompanyAdmin;
  const isSalesRep = roleName === 'Sales Rep';
  const isViewer = roleName === 'Viewer';

  const value = {
    permissions,
    loading,
    error,
    hasPermission,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
    canExport,
    roleName,
    isSuperAdmin,
    isCompanyAdmin,
    isManager,
    isSalesRep,
    isViewer,
    refresh: fetchPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

// Hook to use permissions
export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    // Return default values when not in context (backward compatibility)
    return {
      permissions: null,
      loading: false,
      error: null,
      hasPermission: () => true,
      canCreate: () => true,
      canRead: () => true,
      canUpdate: () => true,
      canDelete: () => true,
      canExport: () => true,
      roleName: 'Unknown',
      isSuperAdmin: false,
      isCompanyAdmin: false,
      isManager: false,
      isSalesRep: false,
      isViewer: false,
      refresh: () => {},
    };
  }
  return context;
}

// Fallback permissions based on user role (when API fails)
function getFallbackPermissions(user) {
  if (!user) return {};
  
  const role = user.role_name || '';
  const isSuperAdmin = user.is_super_admin;

  if (isSuperAdmin || role === 'Super Admin') {
    return {
      is_super_admin: true,
      leads: ['create', 'read', 'update', 'delete'],
      bookings: ['create', 'read', 'update', 'delete'],
      masters: ['create', 'read', 'update', 'delete'],
      reports: ['read', 'export'],
      users: ['create', 'read', 'update', 'delete'],
      settings: ['read', 'update'],
      service: ['create', 'read', 'update', 'delete'],
    };
  }

  if (role === 'Company Admin') {
    return {
      is_super_admin: false,
      leads: ['create', 'read', 'update', 'delete'],
      bookings: ['create', 'read', 'update', 'delete'],
      masters: ['create', 'read', 'update', 'delete'],
      reports: ['read', 'export'],
      users: ['create', 'read', 'update', 'delete'],
      settings: ['read', 'update'],
      service: ['create', 'read', 'update', 'delete'],
    };
  }

  if (role === 'Manager') {
    return {
      is_super_admin: false,
      leads: ['create', 'read', 'update'],
      bookings: ['create', 'read', 'update'],
      masters: ['read'],
      reports: ['read', 'export'],
      users: ['read'],
      settings: ['read'],
      service: ['create', 'read', 'update'],
    };
  }

  if (role === 'Sales Rep') {
    return {
      is_super_admin: false,
      leads: ['create', 'read', 'update'],
      bookings: ['create', 'read', 'update'],
      masters: ['read'],
      reports: ['read'],
      settings: ['read'],
    };
  }

  if (role === 'Viewer') {
    return {
      is_super_admin: false,
      leads: ['read'],
      bookings: ['read'],
      masters: ['read'],
      reports: ['read'],
    };
  }

  // Default: minimal permissions
  return {
    is_super_admin: false,
    leads: ['read'],
    bookings: ['read'],
    masters: ['read'],
    reports: ['read'],
  };
}

export default PermissionsContext;
